<script setup lang="ts">
import type { TSectionCategoryFormOption } from "@/app/tools/new-section-management/section-form/section-form.types";
import type {
  TOpenedCreateSectionDrawerMethod,
  TSiteSectionsTreeData,
} from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { TSiteDetails } from "@/data/api/kili/sites/types/sites.type";

import { type IVirtualizedTreeNode } from "@voltalis/ui-kit/components";
import { CubeSmallBlueIcon, LongArrowDownRightIcon, TimesIcon } from "@voltalis/ui-kit/icons";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

import { useCreateSectionMutation } from "@/composables/kili/sections/mutations/use-create-section-mutation";
import { useUpdateSectionsCategoriesMutation } from "@/composables/kili/sections/mutations/use-update-sections-categories-mutation";
import { useSiteSectionCategory } from "@/composables/kili/sections/use-site-section-category";
import { useToaster } from "@/composables/layout/use-toaster";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EV4bErrorCode } from "@/data/api/kili/common/kili-errors.type";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { getV4bErrorCode } from "@/utils/errors/v4b-errors-utils";

import Skeleton from "primevue/skeleton";

import SectionForm from "@/app/tools/new-section-management/section-form/section-form.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  isLoading?: boolean;
  siteDetails?: TSiteDetails;
  maybePreSelectedParent: TSiteSectionsTreeData;
  siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
  sectionCategoryOptions?: readonly TSectionCategoryFormOption[];
  openedCreateSectionDrawerMethod: TOpenedCreateSectionDrawerMethod | undefined;
}

const props = defineProps<IProps>();
const emits = defineEmits<{
  (e: "select-parent", parent: TSiteSectionsTreeData): void;
}>();
const visible = defineModel<boolean>("visible", {
  required: true,
});

const { t, k } = useTypedIntl();
const toast = useToaster();

const siteSectionForm = useTemplateRef("siteSectionForm");

const computedSiteId = computed(() => props.siteDetails?.siteId);

const { mutateAsync: createSection, isPending: isCreatingSection } = useCreateSectionMutation(computedSiteId, {
  onError: (error) => {
    if (getV4bErrorCode(error) === EV4bErrorCode.SITE_SECTION_ALREADY_EXISTS_WITH_SAME_NAME) {
      siteSectionForm.value?.setSiteSectionsFieldsetCustomError("name", "section_name_already_used");
    }
  },
});
const { createSiteSectionCategory, isCreatingSectionCategory } = useSiteSectionCategory(computedSiteId);
const { mutateAsync: updateSectionsCategories, isPending: isUpdatingSectionsCategories } =
  useUpdateSectionsCategoriesMutation(computedSiteId);

const selectedParent = ref<TSiteSectionsTreeData>(props.maybePreSelectedParent);
const isSubmitting = computed<boolean>(
  () =>
    props.isLoading || isCreatingSection.value || isUpdatingSectionsCategories.value || isCreatingSectionCategory.value,
);

function handleSelectParent(parent: TSiteSectionsTreeData) {
  selectedParent.value = parent;
  emits("select-parent", parent);
}

async function submit() {
  const values = await siteSectionForm.value?.submit();

  if (!values) return;

  try {
    const createdSection = await createSection({
      name: values.newValues.name,
      area: values.newValues.area ?? undefined,
      parentId: values.newValues.parent?.id ?? undefined,
    });

    const maybeAssignedCategoryId = values.newValues.categoryId;

    if (maybeAssignedCategoryId) {
      await updateSectionsCategories({
        sectionsWithCategories: [
          {
            sectionId: createdSection.id,
            sectionCategoryId: maybeAssignedCategoryId,
          },
        ],
      });

      MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_ASSIGNED_TO_SECTION, {
        action_type: "single",
        action: "assigned",
        space_id: createdSection.id,
        category_id: maybeAssignedCategoryId,
      });
    }

    toast.add({
      severity: "success",
      detail: t(k.tools.siteManagement.siteTreeStructure.creation.form.message.success),
    });

    visible.value = false;

    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CREATED, {
      creation_method: props.openedCreateSectionDrawerMethod,
      parent_space_id: values.newValues.parent?.id,
      space_id: createdSection.id,
    });
  } catch (error) {
    toastKiliError(error, "tools.siteManagement.siteTreeStructure.creation.form.message.error");
  }
}

watch(
  () => props.maybePreSelectedParent,
  (newValue) => {
    selectedParent.value = newValue;
  },
  { immediate: true },
);

watch(visible, async (newVal) => {
  if (newVal) {
    await nextTick();

    await siteSectionForm.value?.focusNameInput();
    return;
  }

  siteSectionForm.value?.resetForm();
});
</script>

<template>
  <div
    class="create-site-section-drawer"
    :class="{ visible: visible }"
    :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.component"
  >
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <CubeSmallBlueIcon />
          <h5 class="font-medium text-nowrap">
            {{ t(k.tools.siteManagement.siteTreeStructure.creation.drawer.title) }}
          </h5>
        </div>

        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.closeButton"
          :icon="TimesIcon"
          severity="tertiary"
          size="xs"
          @click="visible = false"
        />
      </div>
      <div
        v-if="selectedParent"
        class="flex items-baseline gap-1 text-gray-600"
      >
        <LongArrowDownRightIcon />
        <span>{{ t(k.tools.siteManagement.siteTreeStructure.creation.drawer.in) }}</span>
        <h5
          class="text-gray-600 font-medium text-nowrap"
          :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.targetSectionName"
        >
          {{ selectedParent.name }}
        </h5>
      </div>
    </div>

    <div class="overflow-y-auto basis-0 flex flex-1 w-full">
      <Skeleton
        v-if="!props.siteDetails"
        class="!w-full !h-full"
        :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.skeleton"
      />

      <SectionForm
        ref="siteSectionForm"
        :is-loading="isSubmitting"
        :initialSelectedParent="props.maybePreSelectedParent"
        :category-options="props.sectionCategoryOptions"
        :create-category="createSiteSectionCategory"
        :is-creating-category="isCreatingSectionCategory"
        :site-details="props.siteDetails"
        :site-sections-tree="props.siteSectionsTree"
        @select-parent="handleSelectParent"
        @submit="submit"
      />
    </div>

    <div class="flex flex-col w-full gap-3">
      <HospitalityButton
        :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.submitButton"
        :label="t(k.tools.siteManagement.siteTreeStructure.creation.button)"
        :loading="isSubmitting"
        size="sm"
        @click="submit"
      />
      <HospitalityButton
        class="text-nowrap"
        :data-test-id="DATA_TEST_IDS.components.createSiteSectionDrawer.cancelButton"
        :label="t(k.global.misc.cancel)"
        outlined
        severity="secondary"
        size="sm"
        @click="visible = false"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.create-site-section-drawer {
  @apply flex flex-col bg-white rounded-2xl w-[324px] p-6 h-full gap-6 transition-all ease-in-out duration-200 ml-8 overflow-hidden;

  &:not(.visible) {
    @apply w-0 px-0 overflow-hidden ml-0;
  }

  &.visible {
    @apply shrink-0;
  }
}
</style>
